import React, { useState, useReducer, useEffect } from 'react'
import styled from 'styled-components'
import theme from '../styling/theme'
import MainGrid from '../styling/MainGrid'
import ReactSlider from 'react-slider'
import Button from '../shared/Button/Button'
import { inRange } from '../../util/functions'

interface IPriceLevel {
  userLabel: string
  onePrice: number
  threePrice: number
}

const LandingPagePricingSlider = ({ data: { attrs } }: any) => {
  const data = {
    overLabel: attrs['over-label'],
    title: attrs.title,
    content: attrs.content,
    copyA: attrs['one-year-card-copy'],
    copyB: attrs['three-year-card-copy'],
    optionsCopy: attrs['options-copy'],
    prices: JSON.parse(decodeURIComponent(attrs['price-info'])),
  } as const

  const maxUsers = attrs['slide-max-users']
  const basePrice: IPriceLevel = data.prices[0]
  const labels: string[] = data.prices.map((price) => price.userLabel)

  const [months, setMonths] = useState<number>(12)
  const [users, setUsers] = useState<number>(1)
  const [priceLevel, setPriceLevel] = useState<IPriceLevel>(basePrice)

  const changeUsers = (e: number) => {
    let userCount = e

    // matches the userCount to the priceLevel by parsing the userLabel
    // and checking if the userCount is within the range specified by the userLabel
    labels.map((label: string) => {
      const [min, max] = label.split('-')

      if (inRange(userCount, min, max)) {
        // console.log(`users: ${userCount} in range ${label}`)

        data.prices.map((price: IPriceLevel) => {
          if (price.userLabel === label) {
            // console.log(`matched ${label} with price ${JSON.stringify(price)}`)
            setPriceLevel(price)
          }
        })
      }
    })
    setUsers(userCount)
  }

  const setMonthsValue = (e: any) => {
    setMonths(parseInt(e.target.value))
  }

  const estimatedPrice = (): number => {
    // if user count is within the base price bracket
    if (basePrice.userLabel === priceLevel.userLabel) {
      return months === 12 ? basePrice.onePrice : basePrice.threePrice
    }

    const [_, max] = basePrice.userLabel.split('-')
    const selectedUsers = users
    
    const baseUsers = parseInt(max)
  
    if(selectedUsers <= 25){
      return 1250
    }

    return  (selectedUsers) * priceLevel.onePrice
    /*
      If somebody has 150 users, they'd be priced at £1250 + 125 users at the £25.99 per user pricing tier.
      As such the total would be £4,498.75
    */
    // const onePrice =
    //   basePrice.onePrice + (selectedUsers - baseUsers) * priceLevel.onePrice
    // const threePrice =
    //   basePrice.threePrice + (selectedUsers - baseUsers) * priceLevel.threePrice

    // return months === 12 ? onePrice : threePrice
  }

  const displayPriceLabel = () => {
    let estimated = estimatedPrice()

    if (months === 36) {
      // let oneYr = estimated *2.5

      estimated = estimated /2 
    }
    const label = estimated.toFixed(2).split('.')

    return (
      <>
        {label[0]} <span>.{label[1]}</span>
      </>
    )
  }

  const displayPriceExplanation = () => {
    const price = estimatedPrice()

    if (months <= 12) {
      return (
        <OneYearDef>
          for <StrongLabel>{users} </StrongLabel>{' '}
          {users === 1 ? 'user' : 'users'}, per year
        </OneYearDef>
      )
    } else {
      return (
        <>
          for <StrongLabelBlack> {users} </StrongLabelBlack>{' '}
          {users === 1 ? 'user' : 'users'} in year one,{" "}
          £{(price).toFixed(2)} thereafter
          <br/>
          {/* <p>For the first year with a 3 year plan,</p>
          <p>then £{price.toFixed(2)} per year</p>
          <p>thereafter. Full 3 year cost £{ (price * 3).toFixed(2) }</p> */}
        </>
      )
    }
  }

  const displayPrices = () => {
    const prices = data.prices.map((price, index) => {
      let one = (
        <p>
          £{price.onePrice.toFixed(2)} {index == 0 ? null : 'per user'}
        </p>
      )

      let three = (
        <>
          <p>
            £{(price.onePrice * 0.5).toFixed(2)}{' '}
            {index == 0 ? null : 'per user'}
          </p>
          <p>
            £{(price.threePrice ).toFixed(2)}{' '}
            {index == 0 ? null : 'per user'}
          </p>
        </>
      )

      let priceTag = months === 12 ? one : three

      return (
        <Price key={index} bold={isBold(price)}>
          <PriceLabel>
            <p>{price.userLabel}</p>
          </PriceLabel>

          <PriceBody val={months === 12}>{priceTag}</PriceBody>
        </Price>
      )
    })

    return prices
  }

  const isBold = (price: IPriceLevel): boolean => {
    if (price.userLabel === priceLevel.userLabel) return true
    else return false
  }

  return (
    <Container>
      <MainGrid>
        <TitleContainer>
          {data.overLabel && <OverLabel>{data.overLabel}</OverLabel>}
          {data.title && <Title>{data.title}</Title>}
          {data.content && <Content>{data.content}</Content>}
        </TitleContainer>

        <OptionsContainer>
          <ButtonsContainer>
            <RadioButton
              type="radio"
              value={12}
              id="1year"
              name="years"
              defaultChecked
              onClick={setMonthsValue}
            />
            <Label htmlFor="1year">1 Year</Label>
            <RadioButton
              type="radio"
              value={36}
              id="3years"
              name="years"
              onClick={setMonthsValue}
            />
            <Label htmlFor="3years">3 Years</Label>
          </ButtonsContainer>

          <OptionsCopy> {data.optionsCopy} </OptionsCopy>
        </OptionsContainer>

        <SliderContainer transform={(users - 1) / 5 + 0.2}>
          <ReactSlider
            className="horizontal-slider"
            thumbClassName="thumb"
            trackClassName="track"
            renderThumb={(props, state) => (
              <div {...props}>
                <div>
                  <div>
                    {state.valueNow}{' '}
                    <span style={{ color: 'white' }}>
                      USER{state.valueNow === 1 ? '' : 'S'}
                    </span>
                  </div>
                </div>
              </div>
            )}
            ariaLabel="Number of users"
            defaultValue={users}
            max={maxUsers}
            min={1}
            onChange={(e) => changeUsers(e)}
          />
        </SliderContainer>

        {months === 36 ? (
          <DiscountSticker>
            <p>50%</p>
            <p>OFF</p>
          </DiscountSticker>
        ) : null}

        <DetailCardsContainer>
          <EstimatedCostContainer>
            <CardLabel>Estimated cost</CardLabel>

            <PriceContainer>
              <EstimatePriceCard>£{displayPriceLabel()}</EstimatePriceCard>
            </PriceContainer>

            <EstimatePriceDefinition>
              {displayPriceExplanation()}
            </EstimatePriceDefinition>

            <CardCopy>{months <= 12 ? data.copyA : data.copyB}</CardCopy>
            <Button href="#">Get a demo</Button>
          </EstimatedCostContainer>

          <PricePerUserContainer>
            <Headings>
              <p>Users</p>
              <div>
                {months === 12 ? (
                  <p>Price</p>
                ) : (
                  <>
                    <p>First year</p>
                    <p>After 1 year</p>
                  </>
                )}
              </div>
            </Headings>

            <PriceContainer>{displayPrices()}</PriceContainer>
          </PricePerUserContainer>
        </DetailCardsContainer>
      </MainGrid>
    </Container>
  )
}

export default LandingPagePricingSlider

const Container = styled.div`
  background: ${theme.text.main};
  padding: 50px 0;
    /* background-image: url(data:image/svg+xml;base64,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);
    background-repeat: no-repeat;
    background-size: auto;
    background-position: bottom; */
  @media (min-width: 1200px) {
    padding: 150px 0;
  }
`

const TitleContainer = styled.div`
  text-align: center;
  padding-bottom: 10px;
`

const OverLabel = styled.p`
  color: white;
  padding-bottom: 10px;
`

const Title = styled.h2`
  color: white;
`

const Content = styled.p`
  color: white;
  padding-bottom: 20px;
`
const OptionsContainer = styled.div`
  text-align: center;
`

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
`

const OptionsCopy = styled.p`
  color: white;
  font-weight: 700;
`

const DiscountSticker = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  height: 90px;
  width: 90px;
  border-radius: 50%;
  background-color: #00a3c7;

  @media only screen and (min-width: 700px) {
    transform: translate(450px, -30px);
  }

  @media only screen and (max-width: 700px) {
    display: none;
  }

  & p {
    color: #fff;
    font-size: 25px;
  }

  & p:first-child {
    font-weight: 700;
  }

  & p:nth-child(2) {
    font-weight: 400;
  }
`

const RadioButton = styled.input`
  &:checked + label {
    background: #00a3c7 !important;
    border: 1px solid #00a3c7 !important;
  }
`
const Label = styled.label`
  border-radius: 40px !important;
  padding: 8px 20px !important;
  min-width: 120px !important;
  color: white !important;
  border: 1px solid white !important;
  background: transparent !important;
  font-weight: 700;
`
const SliderContainer = styled.div`
  max-width: 970px;
  margin: 0 auto;
  margin-top: 75px;

  @media only screen and (max-width: 1199px) {
    max-width: 90%;
  }

  & .horizontal-slider {
    max-width: 970px;
    height: 50px;
    margin: 0 auto;
  }

  & .thumb {
    background: ${theme.text.main};
    border: 4px solid rgb(216, 0, 84);
    height: 30px;
    width: 30px;
    border-radius: 50%;
    cursor: pointer;
    bottom: 66.75%;
    position: relative;
    outline: none;

    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;

    & > div {
      position: absolute;
      top: -50px;
      left: 50%;
      transform: translateX(-50%);
    }

    & > div > div {
      font-weight: bold;
      font-size: 14px;
      background: rgb(216, 0, 84);
      color: white;
      border-radius: 2px;
      display: flex;
      padding: 1px 12px;

      -webkit-touch-callout: none;
      -webkit-user-select: none;
      -khtml-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;

      & span {
        font-weight: 300;
        margin-left: 5px;
        font-size: 14px;
        position: relative;

        -webkit-touch-callout: none;
        -webkit-user-select: none;
        -khtml-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;

        &:after {
          position: absolute;
          content: '';
          bottom: -9px;
          right: 50%;
          width: 0;
          height: 0;
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;

          border-top: 8px solid rgb(216, 0, 84);
        }
      }
    }
  }

  & .track {
    max-width: 970px;
    height: 3px;
    background: #e2e2e2;
    cursor: pointer;
  }

  & .track-0 {
    background: rgb(216, 0, 84);
    position: relative;
    border-left: 4px solid white;

    &:before {
      height: 21px;
      width: 3px;
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      background: rgb(216, 0, 84);
      margin-top: -9px;

      -webkit-touch-callout: none;
      -webkit-user-select: none;
      -khtml-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
    }
  }

  & .track-1 {
    position: relative;
    border-right: 4px solid white;

    &:before {
      height: 21px;
      width: 3px;
      content: '';
      position: absolute;
      right: 0;
      top: 0;
      background: #e2e2e2;
      margin-top: -9px;

      -webkit-touch-callout: none;
      -webkit-user-select: none;
      -khtml-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
    }
  }
`

const DetailCardsContainer = styled.div`
  display: grid;
  grid-template-rows: repeat(2 1fr);
  width: 80%;
  margin: 0 auto;

  @media (min-width: 1050px) {
    grid-template-rows: 1fr;
    grid-template-columns: repeat(2, 1fr);
    justify-content: center;
    align-items: center;
    grid-column-gap: 30px;
  }
`

const EstimatedCostContainer = styled.div`
  border-radius: 3px;
  background: white;
  padding: 50px 30px;
  height: 100%;
`

const PricePerUserContainer = styled.div`
  border-radius: 3px;
  border: 1px solid white;
  background: ${theme.text.main};
  padding: 50px 30px;
  height: 100%;
`

const CardLabel = styled.p`
  font-weight: 700;
`

const Estimation = styled.p`
  color: ${theme.brand.blue};
  font-size: 90px;
  line-height: 90px;
  font-weight: 200;
`
const StrongLabel = styled.span`
  color: ${theme.brand.blue};
  font-weight: 700;
`
const StrongLabelBlack = styled.span`
  font-weight: 700;
`

const EstimatePriceDefinition = styled.p``

const OneYearDef = styled.span`
  color: ${theme.brand.blue};
`
const CardCopy = styled.p`
  padding-bottom: 20px;
`
const Headings = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 5px;
  border-bottom: 1px solid #d9d9d9;

  & p {
    color: white;
    font-size: 11px !important;
    line-height: 13px !important;
    font-weight: bold !important;
  }
  & div p :last-of-type {
    padding-right: 0px;
    min-width: 110px;
    text-align: right;
  }

  & div {
    display: flex;
    justify-content: flex-end;
    column-gap: 10px;
  }
`

const PriceContainer = styled.div`
  padding-top: 5px;
  // max-width: 300px;
`

const Price = styled.div`
  display: flex;
  justify-content: space-between;
  color: white;

  p {
    font-size: 13px !important;
    font-weight: 300;
    padding: 2px 0;
    color: white;

    ${(props) => props.bold && `font-weight: bold !important;`}
  }

  & div {
    display: flex;
    justify-content: space-between;
    column-gap: 10px;
    /* width: 40%; */
  }
`
const PriceLabel = styled.div`
  width: 60%;
`
const PriceBody = styled.div`
  width: ${({ val }) => (val ? null : '50%')};
  white-space: nowrap;
  justify-content: end !important;

  & p:last-of-type {
    min-width: 110px !important;
    text-align: right;
  }
`

const EstimatePriceCard = styled.div`
  display: flex;
  justify-content: start;
  color: ${theme.brand.blue};
  font-size: 60px !important;
  line-height: 60px;
  font-weight: 700;

  & span {
    font-weight: 300;
    color: ${theme.brand.blue};
  }
`
